import { makeAutoObservable, reaction,runInAction } from 'mobx';
import { Notification } from 'ksnm-common-ui/lib/Notification';

import { cmApi } from '@src/api';
import type { AccountInfoDto, KeycloakUserDto } from '@src/api';

import { constantStore } from '../constantStore';
import { preloaderStore } from '../preloaderStore';

type SortType = {
  key: string;
  direction: number;
};

enum UsersForExtraView {
  SIDOROVDV,
  AKULOV,
}

export type FilterType = Record<string, (string | number)[]>;

export type FilterRangeType = Record<string, string[]>;

export type ScrollConfig = Record<string, number>;

interface ITable {
  filters?: FilterType;
  filterRange?: FilterRangeType;
  sort?: SortType[];
  visibleColumns?: string[];
}

interface IUserConfig {
  tables: Record<string, ITable>;
  sideMenuSize?: string | null;
}

export type Scope = 'READ' | 'DELETE' | 'CREATE' | 'UPDATE' | 'VIEW';

const RESOURCE_NAME_ROUTES_KEYS = Object.keys(constantStore.RESOURCE_NAME_ROUTES) as Array<
  keyof typeof constantStore.RESOURCE_NAME_ROUTES
>;

export type ResourceNameRoutesKeys = (typeof RESOURCE_NAME_ROUTES_KEYS)[number];

const RESOURCE_NAME_KEYS = Object.keys(constantStore.ALL_RESOURCE_NAME) as Array<
  keyof typeof constantStore.ALL_RESOURCE_NAME
>;

export type ResourceNameKeys = (typeof RESOURCE_NAME_KEYS)[number];

export type UserPermission = {
  resourceName?: ResourceNameKeys;
  scopes?: Scope[];
};

export type User = Omit<KeycloakUserDto, 'permissions'> & {
  fio?: string;
  permissions?: UserPermission[];
};

export type ScopePermissions = {
  [key in Scope]: boolean;
};

export type UserPermissions = {
  [key in ResourceNameKeys]?: {
    scopePermissions: ScopePermissions;
  };
};

class UserStore {
  user: User | null = null;

  tableScrollTop = 0;

  extraView = false;

  config: IUserConfig;

  isLoading = false;

  permissions: UserPermissions | undefined = undefined;

  accountInfo: AccountInfoDto | undefined = undefined;

  constructor() {
    let jsonDefaultConfig = {
      tables: {},
      sideMenuSize: 'large',
    };
    try {
      jsonDefaultConfig = JSON.parse(localStorage.getItem('config') ?? JSON.stringify(jsonDefaultConfig));
    } catch {
      localStorage.removeItem('config');
      Notification({
        message: 'Ошибка конфигурации пользователя',
        type: 'info',
      });
    }

    this.config = { ...jsonDefaultConfig, tables: {} };
    makeAutoObservable(this);
  }

  changeMenuSizeMode = () => {
    this.config = {
      ...this.config,
      sideMenuSize: this.config.sideMenuSize === 'large' ? 'small' : 'large',
    };
  };

  private getPermissions = (data: Pick<User, 'permissions'>): UserPermissions => {
    let finalObj: UserPermissions = {};

    data.permissions?.forEach((el) => {
      if (el.scopes && el.resourceName) {
        const permissions = {
          [el.resourceName]: {
            scopePermissions: { READ: false, DELETE: false, UPDATE: false, CREATE: false, VIEW: false },
          },
        };
        for (const scope of el.scopes) {
          permissions[el.resourceName].scopePermissions[scope] = !!scope;
        }
        finalObj = {
          ...finalObj,
          ...permissions,
        };
      }
    });
    return finalObj;
  };

  getUserInfo = async (): Promise<KeycloakUserDto | null> => {
    preloaderStore.preloader(true, 'Загрузка...');
    const { data } = await cmApi.currentUser.getCurrentUser();

    if (data) {
      const { lastName, firstName, middleName, userLogin } = data;

      runInAction(() => {
        this.user = {
          ...data,
          fio: `${lastName ?? ''} ${firstName ?? ''} ${middleName ?? ''}`,
        };
        this.permissions = this.getPermissions({ ...data });

        if (
          Object.keys(UsersForExtraView)
            .map((key) => key.toLowerCase())
            .includes(userLogin ?? '')
        ) {
          this.extraView = true;
        }
      });
    }
    preloaderStore.preloader(false);
    return data;
  };

  getAccountInfo = async () => {
    preloaderStore.preloader(true, 'Загрузка...');
    const { data } = await cmApi.account.getAccountInfo();

    if (data) {
      runInAction(() => {
        this.accountInfo = data;
      });
    }

    preloaderStore.preloader(false);
  };
}

export const userStore = new UserStore();

reaction(
  () => userStore.config.sideMenuSize,
  (config) => {
    localStorage.setItem('config', JSON.stringify(config));
  },
);
